import {
  ConnectionHandler,
  Environment,
  graphql,
  useMutation,
} from "react-relay";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { logger } from "@/common/logger";
import { AddMemberForm } from "@/components/AddMemberForm";
import OrganizationUsers from "@/components/OrganizationUsers";
import { toast } from "@/utils/toast";
import ErrorPage from "@/pages/_error";
import { OrganizationUsersPageAddMutation as OrganizationUsersPageAddMutationType } from "./__generated__/OrganizationUsersPageAddMutation.graphql";
import { LoaderArgs } from "@/utils/route";
import { OrganizationUsersPageQuery as OrganizationUsersPageQueryType } from "./__generated__/OrganizationUsersPageQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
const OrganizationUsersPageAddMutation = graphql`
  mutation OrganizationUsersPageAddMutation(
    $organizationId: ID!
    $userId: ID!
    $kind: OrganizationMembershipKind!
    $connections: [ID!]!
  ) {
    updateOrganizationMembership(
      organizationId: $organizationId
      userId: $userId
      kind: $kind
    ) @prependEdge(connections: $connections) {
      node {
        id
        kind
        viewerCanDelete: viewerCan(action: REMOVE_ORGANIZATION_MEMBER)
        user {
          username
          displayName
          ...EntityProfilePicFragment @arguments(thumbnail: true)
        }
        organization {
          viewerCanEdit: viewerCan(action: UPDATE_ORGANIZATION_MEMBERSHIP)
        }
      }
    }
  }
`;
export default function OrganizationUsersPage() {
  const intl = useIntl();
  const { variables, query } = useOrganizationUsersPageQuery();
  const [commitAddMutation, isAddMutationInFlight] =
    useMutation<OrganizationUsersPageAddMutationType>(
      OrganizationUsersPageAddMutation,
    );
  if (!query.entityByUsername) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage(
          { defaultMessage: "@{username} not found" },
          { username: variables.username },
        )}
      />
    );
  }
  const organization = query.entityByUsername;
  return (
    <div>
      {organization.viewerCanEdit && (
        <div className="flex flex-col items-end pb-8">
          <AddMemberForm
            submitting={isAddMutationInFlight}
            onSubmit={({ entityId: userId }, form) => {
              commitAddMutation({
                variables: {
                  organizationId: organization.id,
                  userId,
                  kind: "ADMIN",
                  connections: [
                    ConnectionHandler.getConnectionID(
                      organization.id,
                      "OrganizationUsersFragment_users",
                    ),
                    ConnectionHandler.getConnectionID(
                      userId,
                      "UserOrganizationsFragment_organizations",
                    ),
                    ConnectionHandler.getConnectionID(
                      organization.id,
                      "CompetitionTeamSelectOrganizationUsersFragment_users",
                    ),
                  ],
                },
                onError: (error) => {
                  logger.error(error);
                  toast.error(
                    <FormattedMessage defaultMessage="Something went wrong" />,
                  );
                },
                onCompleted: ({
                  updateOrganizationMembership: {
                    node: { user },
                  },
                }) => {
                  form.reset();
                  toast.success(
                    <FormattedMessage
                      defaultMessage={`"{user}" has been added to the organization`}
                      values={{ user: user.username }}
                    />,
                  );
                },
              });
            }}
          />
        </div>
      )}
      <OrganizationUsers user={organization} />
    </div>
  );
}
const OrganizationUsersPageQuery = graphql`
  query OrganizationUsersPageQuery($username: String!) {
    entityByUsername(username: $username) {
      id
      ... on Organization {
        viewerCanEdit: viewerCan(action: UPDATE_ORGANIZATION_MEMBERSHIP)
        ...OrganizationUsersFragment
      }
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { username } }: LoaderArgs) => {
    if (!username) {
      throw new Response("No username", { status: 500 });
    }
    return preload<OrganizationUsersPageQueryType>(
      environment,
      OrganizationUsersPageQuery,
      {
        username,
      },
    );
  };
};
const useOrganizationUsersPageQuery = () =>
  usePreloaded<OrganizationUsersPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Members" }),
};
